import React, {
  useContext,
  useRef,
  useMemo,
  useCallback,
  FC,
  useEffect,
} from 'react';
import { Form, FormApi, useFormState } from 'informed';
import { useTranslation } from 'react-i18next';
import {
  AuthContext,
  clearBonusCode,
  DepositBonusCode,
  setBonusCode,
  updateBonusSettings,
  useApi,
  uuid,
} from '@repo-level/core-front';
import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';

import { useAnalytics } from 'helpers/analytics';
import { AnalyticsEventName } from 'api/analytics';
import { Button } from 'components/buttons';
import { Checkbox } from 'components/form/Checkbox';
import { FormGroup } from 'components/form/groups/FormGroup';
import { Input } from 'components/form/Input';
import { required } from 'helpers/validators';
import { Spinner } from 'components/Spinner';

import { ScInputPromo } from './styled';

interface InputPromoProps {
  pending: boolean;
}

export const InputPromo: FC<InputPromoProps> = ({ pending }) => {
  const { t } = useTranslation();
  const { sendEventToAnalytics } = useAnalytics();
  const { user, fetchUser, pendingUser } = useContext(AuthContext);

  const canIssueBonuses = useMemo(() => user?.canIssueBonuses, [user]);

  const formApiRef = useRef<FormApi<DepositBonusCode>>();

  const [
    requestUpdateBonusSettings,
    { pending: pendingUpdateBonusSettings },
  ] = useApi(updateBonusSettings);

  const [requestsSetBonusCode, { pending: pendingSetBonusCode }] = useApi(
    setBonusCode
  );

  const [requestClearBonusCode, { pending: pendingClearBonusCode }] = useApi(
    clearBonusCode
  );

  const handleChangeReceiveBonuses = useCallback(async () => {
    const { error } = await requestUpdateBonusSettings(!canIssueBonuses)
      .request;

    if (error != null) return;

    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__DEPOSIT__CAN_ISSUE_BONUSES__CLICK,
      attributes: {
        additionalData: JSON.stringify({ canIssue: !canIssueBonuses }),
      },
    });

    fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
  }, [
    requestUpdateBonusSettings,
    canIssueBonuses,
    sendEventToAnalytics,
    fetchUser,
  ]);

  const handleSubmitCode = useCallback(
    async (values: DepositBonusCode) => {
      const { error } = await requestsSetBonusCode(values).request;

      if (error != null || formApiRef.current == null) return;

      formApiRef.current.setValue('depositBonusCode', values.depositBonusCode);
      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__DEPOSIT__PROMO_CODE_FORM__ADD,
        attributes: {
          additionalData: JSON.stringify({
            depositBonusCode: values.depositBonusCode,
          }),
        },
      });
      fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
    },
    [requestsSetBonusCode, fetchUser, sendEventToAnalytics]
  );

  const handleRemoveCode = useCallback(async () => {
    const { error } = await requestClearBonusCode().request;

    if (error != null) return;

    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__DEPOSIT__PROMO_CODE_FORM__CANCEL,
    });
    fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
  }, [requestClearBonusCode, fetchUser, sendEventToAnalytics]);

  return (
    <ScInputPromo>
      {pendingUpdateBonusSettings || pendingUser || pending ? (
        <Spinner size={64} center />
      ) : (
        <>
          <FormGroup>
            <Checkbox
              key={uuid()}
              label={t('deposits:inputPromo.dontWantBonuses')}
              field="notReceiveBonuses"
              initialValue={!canIssueBonuses}
              onClick={handleChangeReceiveBonuses}
            />
          </FormGroup>

          {canIssueBonuses && (
            <Form<DepositBonusCode>
              getApi={(api) => void (formApiRef.current = api)}
              onSubmit={handleSubmitCode}
            >
              <Analytics />
              <FormGroup title={t('deposits:inputPromo.formGroup')}>
                <div className="form-group__row">
                  <div className="form-group__input">
                    <Input
                      field="depositBonusCode"
                      validate={required}
                      initialValue={user?.depositBonusCode || undefined}
                      disabled={user?.depositBonusCode != null}
                    />
                  </div>
                  <div className="form-group__button">
                    {user?.depositBonusCode != null ? (
                      <Button
                        green
                        xLarge
                        onClick={handleRemoveCode}
                        pending={pendingClearBonusCode}
                      >
                        {t('glossary:remove')}
                      </Button>
                    ) : (
                      <Button
                        green
                        xLarge
                        type="submit"
                        pending={pendingSetBonusCode}
                      >
                        {t('glossary:add')}
                      </Button>
                    )}
                  </div>
                </div>
              </FormGroup>
            </Form>
          )}
        </>
      )}
    </ScInputPromo>
  );
};

const Analytics = () => {
  const { touched } = useFormState();
  const touchedForm = Object.values(touched).some((t) => t);
  const { sendEventToAnalytics } = useAnalytics();

  useEffect(() => {
    if (touchedForm) {
      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__DEPOSIT__PROMO_CODE_FORM__TOUCHED,
      });
    }
  }, [touchedForm, sendEventToAnalytics]);

  return null;
};
